import React, { useEffect, useState } from 'react';
import { KeyRound, Plus, Loader2, Copy, ShieldOff, RefreshCw, BookOpen } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AutomationPanel } from '@/components/admin/AutomationPanel';

const DOCS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/integrations/publisher/v1/docs`;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const AdminIntegrations = () => {
  const [keys, setKeys] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [logKey, setLogKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);

  const load = async () => {
    try {
      const [k, l, s] = await Promise.all([
        api.getIntegrationKeys(),
        api.getIntegrationLogs({ limit: 100, key_id: logKey || undefined }),
        api.getIntegrationStats(),
      ]);
      setKeys(k.data); setLogs(l.data); setStats(s.data);
    } catch { toast.error('Error al cargar integraciones'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [logKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeKeys = keys.filter(k => !k.revoked_at);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.createIntegrationKey(newName.trim());
      setCreatedKey(res.data);
      setNewName('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear la clave');
    } finally { setCreating(false); }
  };

  const handleRevoke = async (key) => {
    if (!window.confirm(`¿Revocar la clave "${key.name}"? El bot dejará de poder escribir con ella.`)) return;
    try {
      await api.revokeIntegrationKey(key.id);
      toast.success('Clave revocada');
      load();
    } catch { toast.error('No se pudo revocar'); }
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(createdKey.api_key); toast.success('Clave copiada'); }
    catch { toast.error('Copia la clave manualmente'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#38B2AC]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-integrations">
      <AutomationPanel />
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-semibold text-lg flex items-center gap-2"><KeyRound className="text-[#38B2AC]" size={20} /> Claves API «Guía Publisher»</h2>
            <p className="text-sm text-gray-500">Scope <code className="bg-gray-100 px-1 rounded">content:admin</code> · máx. 2 activas · 120 peticiones/min · la clave se muestra una sola vez.</p>
          </div>
          <a href={DOCS_URL} target="_blank" rel="noreferrer" className="text-sm text-[#38B2AC] inline-flex items-center gap-1 hover:underline" data-testid="publisher-docs-link">
            <BookOpen size={16} /> Documentación OpenAPI
          </a>
        </div>

        <div className="flex gap-2 mb-4">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nombre de la clave (p. ej. Guía Publisher #1)"
            className="rounded-xl" data-testid="new-key-name-input" onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
          <Button onClick={handleCreate} disabled={creating || activeKeys.length >= 2 || !newName.trim()}
            className="bg-[#38B2AC] hover:bg-[#005A63] rounded-xl whitespace-nowrap" data-testid="create-key-btn">
            {creating ? <Loader2 className="animate-spin mr-1" size={16} /> : <Plus size={16} className="mr-1" />} Crear clave
          </Button>
        </div>
        {activeKeys.length >= 2 && <p className="text-xs text-amber-600 mb-3" data-testid="max-keys-warning">Ya hay 2 claves activas. Revoca una para rotar.</p>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="keys-table">
            <thead className="text-left text-xs text-gray-500 border-b">
              <tr><th className="py-2">Nombre</th><th>Prefijo</th><th>Creada</th><th>Último uso</th><th>Estado</th><th></th></tr>
            </thead>
            <tbody>
              {keys.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-gray-400">Sin claves todavía</td></tr>}
              {keys.map(k => (
                <tr key={k.id} className="border-b last:border-0" data-testid={`key-row-${k.id}`}>
                  <td className="py-2 font-medium">{k.name}</td>
                  <td><code className="text-xs bg-gray-100 px-1 rounded">{k.key_prefix}…</code></td>
                  <td className="text-gray-500">{fmt(k.created_at)}</td>
                  <td className="text-gray-500">{fmt(k.last_used_at)}</td>
                  <td>{k.revoked_at
                    ? <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Revocada</span>
                    : <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Activa</span>}</td>
                  <td className="text-right">
                    {!k.revoked_at && (
                      <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handleRevoke(k)} data-testid={`revoke-key-btn-${k.id}`}>
                        <ShieldOff size={14} className="mr-1" /> Revocar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="draft-stats">
          {Object.entries(stats).map(([res, s]) => (
            <div key={res} className="bg-white rounded-xl p-4 shadow-sm">
              <p className="text-xs text-gray-500 capitalize">{res}</p>
              <p className="text-sm"><span className="font-semibold text-amber-600">{s.draft}</span> borradores · <span className="text-gray-500">{s.archived} archivados</span></p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Registro de accesos (30 días)</h3>
          <div className="flex items-center gap-2">
            <select value={logKey} onChange={(e) => setLogKey(e.target.value)} className="h-8 rounded-lg border border-gray-200 text-sm px-2" data-testid="log-key-filter">
              <option value="">Todas las claves</option>
              {keys.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
            <Button variant="ghost" size="icon" onClick={load} data-testid="refresh-logs-btn"><RefreshCw size={16} /></Button>
          </div>
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-xs" data-testid="logs-table">
            <thead className="text-left text-gray-500 border-b sticky top-0 bg-white">
              <tr><th className="py-1">Fecha</th><th>Clave</th><th>Método</th><th>Ruta</th><th>Resultado</th><th>ms</th></tr>
            </thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-gray-400">Sin accesos registrados</td></tr>}
              {logs.map(l => (
                <tr key={l.id} className="border-b last:border-0">
                  <td className="py-1 text-gray-500 whitespace-nowrap">{fmt(l.created_at)}</td>
                  <td>{l.key_name}</td>
                  <td className="font-mono">{l.method}</td>
                  <td className="font-mono truncate max-w-[280px]">{l.path.replace('/api/integrations/publisher/v1', '')}</td>
                  <td><span className={`px-1.5 rounded ${l.status < 300 ? 'bg-emerald-50 text-emerald-700' : l.status < 500 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{l.status}</span></td>
                  <td className="text-gray-400">{l.duration_ms}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!createdKey} onOpenChange={(o) => !o && setCreatedKey(null)}>
        <DialogContent className="bg-white" data-testid="created-key-dialog">
          <DialogHeader><DialogTitle>Clave creada: {createdKey?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Cópiala ahora y entrégala al bot por un canal seguro. <strong>No volverá a mostrarse.</strong></p>
          <div className="flex items-center gap-2 bg-gray-50 border rounded-xl p-3">
            <code className="text-xs break-all flex-1" data-testid="created-key-value">{createdKey?.api_key}</code>
            <Button variant="ghost" size="icon" onClick={copyKey} data-testid="copy-key-btn"><Copy size={16} /></Button>
          </div>
          <DialogFooter><Button onClick={() => setCreatedKey(null)} className="bg-[#38B2AC] hover:bg-[#005A63]" data-testid="close-key-dialog-btn">He guardado la clave</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminIntegrations;
